import React, { useState } from "react";
import axios from "axios";
import { Plus, Trash2, Search, CalendarDays, Loader2 } from "lucide-react";
import { API } from "../config";

/**
 * Admin tool for creating an order on behalf of a customer.
 *
 * Features:
 *  - Free-typed customer name / email / address (no account required)
 *  - SKU/name search → add line items with adjustable quantities
 *  - Backdate via custom invoice date
 *  - Payment method dropdown including 30-Day Credit Account
 *  - Computes subtotal, VAT @ 20%, delivery, total live before submit
 */
export default function AdminCreateOrder({ onCreated, onToast }) {
  const today = new Date().toISOString().slice(0, 10);
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "",
    address: "", city: "", postcode: "",
  });
  const [items, setItems] = useState([]); // {product_id, name, sku, price, quantity}
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("credit_30day");
  const [status, setStatus] = useState("awaiting_payment");
  const [createdAt, setCreatedAt] = useState(today);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);

  // Fetch the live credit balance whenever the customer email (or the
  // selected payment method) changes — only relevant for 30-day credit.
  React.useEffect(() => {
    let cancelled = false;
    const email = customer.email.trim();
    if (paymentMethod !== "credit_30day" || !email || email.length < 4 || !email.includes("@")) {
      setCreditInfo(null);
      return undefined;
    }
    const id = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/admin/credit-accounts/${encodeURIComponent(email)}`);
        if (!cancelled) setCreditInfo(res.data);
      } catch {
        if (!cancelled) setCreditInfo(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [customer.email, paymentMethod]);

  const runSearch = async (q) => {
    setSearch(q);
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/products`, {
        params: { search: q.trim(), limit: 8 },
      });
      setResults(res.data || []);
    } catch (err) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addLine = (product) => {
    setItems((prev) => {
      const found = prev.find((p) => p.product_id === product.id);
      if (found) {
        return prev.map((p) => p.product_id === product.id ? { ...p, quantity: p.quantity + 1 } : p);
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        sku: product.sku || "",
        price: product.price,
        quantity: 1,
      }];
    });
    setSearch("");
    setResults([]);
  };

  const setQty = (pid, qty) => {
    setItems((prev) => prev.map((p) => p.product_id === pid ? { ...p, quantity: Math.max(1, qty) } : p));
  };

  const removeLine = (pid) => setItems((prev) => prev.filter((p) => p.product_id !== pid));

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const delivery = subtotal >= 30 || subtotal === 0 ? 0 : 4.99;
  const vat = +((subtotal + delivery) * 0.20).toFixed(2);
  const total = +((subtotal + delivery + vat)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.name || !customer.email) {
      onToast?.({ message: "Customer name and email are required", type: "error" });
      return;
    }
    if (items.length === 0) {
      onToast?.({ message: "Add at least one line item", type: "error" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: customer.name,
        customer_email: customer.email,
        phone: customer.phone,
        shipping_address: customer.address,
        shipping_city: customer.city,
        shipping_postcode: customer.postcode,
        items: items.map(({ product_id, quantity }) => ({ product_id, quantity })),
        payment_method: paymentMethod,
        status,
        created_at: createdAt,
        notes,
      };
      const res = await axios.post(`${API}/admin/orders`, payload);
      onToast?.({ message: `Order #${res.data.id.slice(0, 8).toUpperCase()} created`, type: "success" });
      onCreated?.(res.data);
      // Reset only line items + notes; keep customer details for follow-up orders
      setItems([]);
      setNotes("");
    } catch (err) {
      onToast?.({ message: err.response?.data?.detail || "Failed to create order", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card p-6 space-y-6" data-testid="admin-create-order">
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#00a99d]" /> Create Order / Invoice
        </h3>
        <p className="text-sm text-gray-500 -mt-1">
          Manually log an order on behalf of a customer. Useful for phone/email orders, backdated invoices, or trade accounts on credit.
        </p>
      </div>

      {/* Customer */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Customer name *</label>
          <input type="text" required className="form-input" value={customer.name}
            onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
            data-testid="admin-order-name" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Customer email *</label>
          <input type="email" required className="form-input" value={customer.email}
            onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
            data-testid="admin-order-email" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Phone</label>
          <input type="tel" className="form-input" value={customer.phone}
            onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
            data-testid="admin-order-phone" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-1">
            <CalendarDays className="w-4 h-4" /> Invoice date (backdate ok)
          </label>
          <input type="date" required className="form-input" value={createdAt}
            onChange={(e) => setCreatedAt(e.target.value)}
            data-testid="admin-order-date" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Delivery address</label>
          <input type="text" className="form-input" value={customer.address}
            onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
            data-testid="admin-order-address" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">City</label>
          <input type="text" className="form-input" value={customer.city}
            onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
            data-testid="admin-order-city" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Postcode</label>
          <input type="text" className="form-input" value={customer.postcode}
            onChange={(e) => setCustomer({ ...customer, postcode: e.target.value })}
            data-testid="admin-order-postcode" />
        </div>
      </div>

      {/* Line items */}
      <div>
        <h4 className="font-semibold mb-2">Line items</h4>
        <div className="relative mb-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            <input
              type="text"
              placeholder="Search products by name or SKU…"
              value={search}
              onChange={(e) => runSearch(e.target.value)}
              className="form-input pl-9"
              data-testid="admin-order-search"
            />
          </div>
          {(searching || results.length > 0) && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="admin-order-search-results">
              {searching && <div className="p-3 text-sm text-gray-500">Searching…</div>}
              {results.map((p) => (
                <button type="button" key={p.id} onClick={() => addLine(p)}
                  className="w-full text-left px-3 py-2 hover:bg-teal-50 flex justify-between items-center"
                  data-testid={`admin-order-add-${p.id}`}>
                  <div>
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.sku} · {p.category}</div>
                  </div>
                  <div className="text-sm font-semibold text-[#00a99d]">&pound;{p.price.toFixed(2)}</div>
                </button>
              ))}
              {!searching && results.length === 0 && search.length >= 2 && (
                <div className="p-3 text-sm text-gray-500">No matches</div>
              )}
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-gray-400 italic p-4 border border-dashed rounded-md text-center">
            No items added yet. Search above and click a product to add it.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-gray-500">
              <tr>
                <th className="text-left py-2">Item</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit</th>
                <th className="text-right">Line</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.product_id} className="border-b last:border-b-0">
                  <td className="py-2">
                    <div className="font-medium">{i.name}</div>
                    <div className="text-xs text-gray-500">{i.sku}</div>
                  </td>
                  <td className="text-right">
                    <input type="number" min="1" value={i.quantity}
                      onChange={(e) => setQty(i.product_id, parseInt(e.target.value, 10) || 1)}
                      className="w-16 border rounded px-2 py-1 text-right"
                      data-testid={`admin-order-qty-${i.product_id}`} />
                  </td>
                  <td className="text-right">&pound;{i.price.toFixed(2)}</td>
                  <td className="text-right font-semibold">&pound;{(i.price * i.quantity).toFixed(2)}</td>
                  <td className="text-right">
                    <button type="button" onClick={() => removeLine(i.product_id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`admin-order-remove-${i.product_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Payment + totals */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Payment method</label>
            <select className="form-input" value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              data-testid="admin-order-payment-method">
              <option value="credit_30day">30-Day Credit Account</option>
              <option value="bacs">Bank Transfer (BACS)</option>
              <option value="stripe">Card (Stripe)</option>
              <option value="sumup">Card (SumUp)</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
            {paymentMethod === "credit_30day" && creditInfo && (
              <div className="mt-2 text-xs border rounded p-2 bg-gray-50" data-testid="credit-info-box">
                {creditInfo.exists ? (
                  <>
                    <div className="flex justify-between">
                      <span>Credit limit</span>
                      <span className="font-semibold">&pound;{Number(creditInfo.credit_limit).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Outstanding</span>
                      <span className="font-semibold text-red-600">&pound;{Number(creditInfo.outstanding).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Available</span>
                      <span className="font-semibold text-emerald-700">&pound;{Number(creditInfo.available).toFixed(2)}</span>
                    </div>
                    {total > creditInfo.available && (
                      <div className="mt-1 text-red-600 font-semibold" data-testid="credit-warning">
                        ⚠ This order (&pound;{total.toFixed(2)}) exceeds available credit.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-amber-700">
                    No credit account on file for {customer.email}. Add one in the <em>Credit</em> tab before approving on credit.
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select className="form-input" value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="admin-order-status">
              <option value="awaiting_payment">Awaiting Payment</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Internal notes</label>
            <textarea className="form-input" rows="2" value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — supplier invoice ref, PO number, etc."
              data-testid="admin-order-notes" />
          </div>
        </div>

        <div className="bg-gray-50 rounded-md p-4 h-fit">
          <h4 className="font-semibold mb-3">Totals</h4>
          <dl className="text-sm space-y-1">
            <div className="flex justify-between"><dt>Subtotal (excl. VAT)</dt><dd>&pound;{subtotal.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt>Delivery</dt><dd className={delivery === 0 ? "text-green-600" : ""}>{delivery === 0 ? "FREE" : `\u00A3${delivery.toFixed(2)}`}</dd></div>
            <div className="flex justify-between"><dt>VAT (20%)</dt><dd>&pound;{vat.toFixed(2)}</dd></div>
            <div className="flex justify-between border-t pt-2 mt-2 font-semibold text-base">
              <dt>Total</dt><dd data-testid="admin-order-total">&pound;{total.toFixed(2)}</dd>
            </div>
          </dl>
        </div>
      </div>

      <button type="submit" disabled={submitting} className="btn-primary"
        data-testid="admin-order-submit">
        {submitting ? (
          <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Creating…</span>
        ) : (
          "Create order & invoice"
        )}
      </button>
    </form>
  );
}
